'use client';
import React, { useEffect } from "react";
import "./privacy-policy.modules.css";
import Footer from "@components/footer/Footer";
import Navbar from "@components/Navbar";

const PrivacyPolicy = () => {
  useEffect(() => {
    const collapsibles = document.querySelectorAll(".collapsible");
    collapsibles.forEach((button) => {
      button.addEventListener("click", function () {
        this.classList.toggle("active");
        const content = this.nextElementSibling;
        if (content.style.display === "block") {
          content.style.display = "none";
        } else {
          content.style.display = "block";
        }
      });
    });

    return () => {
      collapsibles.forEach((button) => {
        button.removeEventListener("click", () => { });
      });
    };
  }, []);

  const linkCollapsible = (id) => {
    const targetElement = document.getElementById(id);
    if (targetElement) {
      targetElement.classList.toggle("active");
      const content = targetElement.nextElementSibling;
      if (content.style.display !== "block") {
        content.style.display = "block";
      }
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  return (
    <>
      <Navbar />
      <div>
        {/* Header Section */}
        <header className="header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated December 17, 2025</p>
        </header>

        {/* Main Container */}
        <div className="content">
          {/* Sidebar (Table of Contents) */}
          <aside className="sidebar">
            <h2>On this page</h2>
            <ul>
              <li><a onClick={() => linkCollapsible('key-terms-button')}>Key Terms</a></li>
              <li><a onClick={() => linkCollapsible('your-personal-information-button')}>Your Personal Information</a></li>
              <li><a onClick={() => linkCollapsible('sharing-your-data-button')}>Sharing Your Data</a></li>
              <li><a onClick={() => linkCollapsible('where-is-your-data-button')}>Where In The World Is Your Data?</a></li>
              <li><a onClick={() => linkCollapsible('your-rights-button')}>Your Rights and How to Manage or Delete Your Personal Information</a></li>
              <li><a onClick={() => linkCollapsible('childrens-privacy-button')}>Children&apos;s Privacy</a></li>
              <li><a onClick={() => linkCollapsible('contact-button')}>Contact Us</a></li>
              <li><a onClick={() => linkCollapsible('secure-data-button')}>Keeping and Securing Your Data</a></li>
              <li><a onClick={() => linkCollapsible('supplemental-notices-button')}>Supplemental Notices</a></li>
            </ul>
          </aside>

          {/* Main Content */}
          <main className="main-content">
            <p>
              This Privacy Policy (“Policy”) describes how <strong>PenguinLearn Holdings LLC, doing business as PenguinLearn</strong>
              (“PenguinLearn,” “we,” “us,” “our”) collects, uses, shares, and protects personal information when you use our
              website and services (the “Services”).
            </p>

            <p>
              PenguinLearn is an online education marketplace where Parents/Guardians can enroll Learners in classes taught by
              independent Teachers. This Policy applies to Parents/Guardians, Learners, Teachers, and other users of the Services.
            </p>

            <p>
              Our Services may include links to, integrations with, or use of third-party services (such as video providers).
              Those third parties may have their own privacy practices, and we encourage you to review their privacy notices.
            </p>

            <p>
              We may update this Policy from time to time. If we make material changes, we will post the updated Policy on this page
              and update the “Last updated” date.
            </p>

            <p>
              If you have any questions, contact us at <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
            </p>

            {/* Collapsible Sections */}
            <div className="collapsible-container">
              <button className="collapsible" id="key-terms-button">Key Terms</button>
              <div className="content-collapsible" id="key-terms">
                <p>These terms help explain this Policy:</p>
                <ul>
                  <li><strong>Parent/Guardian</strong>: A parent or legal guardian who creates an account and enrolls a Learner.</li>
                  <li><strong>Learner</strong>: A child under 18 who participates in classes.</li>
                  <li><strong>Teacher</strong>: An independent instructor (individual) who offers classes through PenguinLearn.</li>
                  <li><strong>Services</strong>: Our website, platform, class experience, communications, and related tools.</li>
                  <li><strong>Personal Information</strong>: Information that identifies or can reasonably be linked to a person.</li>
                  <li><strong>Process</strong>: Collecting, using, storing, sharing, or otherwise handling Personal Information.</li>
                </ul>
              </div>

              <button className="collapsible" id="your-personal-information-button">Your Personal Information</button>
              <div className="content-collapsible" id="your-personal-information">
                <p>
                  We aim to collect only the personal information needed to operate PenguinLearn and provide the Services safely.
                </p>

                <p><strong>1) Information you provide</strong></p>
                <p>
                  When you create an account or a Learner profile, we collect: <strong>name, email address, and age</strong>.
                  Teachers may provide additional profile information (such as a profile photo, biography, and subjects taught) if they choose.
                </p>

                <p><strong>2) Information created through the Services</strong></p>
                <p>
                  When you use PenguinLearn, we process information associated with providing and operating the Services, such as:
                  enrollments, class rosters, class messages and communications, submitted work (if any), class feedback or reviews,
                  and support communications.
                </p>

                <p><strong>3) Live classes and recordings (Zoom)</strong></p>
                <p>
                  PenguinLearn uses <strong>Zoom</strong> for live classes. <strong>Classes are recorded</strong> for safety, quality,
                  training, dispute resolution, and support. Recordings may include audio, video, chat messages, and on-screen content
                  shared during a session.
                </p>

                <p><strong>4) Payments (Stripe)</strong></p>
                <p>
                  PenguinLearn uses <strong>Stripe</strong> to process payments. Payment information is collected and processed by Stripe
                  according to Stripe’s privacy practices. PenguinLearn may receive limited payment-related information such as confirmation
                  of payment, transaction identifiers, and purchase status.
                </p>

                <p><strong>5) Teachers and background checks</strong></p>
                <p>
                  Teachers are required to complete a <strong>background check</strong> as part of onboarding and/or ongoing safety requirements.
                  Background checks may be performed by third-party screening providers. Teachers may provide information directly to the provider
                  (and/or to us) as needed to complete the screening. We may receive confirmation of completion and/or results to the extent permitted
                  by law and necessary for platform safety.
                </p>

                <p><strong>6) Emails and communications (third-party service providers)</strong></p>
                <p>
                  We use <strong>third-party service providers</strong> to send service-related messages (such as enrollment confirmations, class updates, and important account notices).
                  Where permitted, we may also send product updates or announcements. You can opt out of marketing emails at any time.
                  Even if you opt out of marketing, we may still send essential service-related messages.
                </p>

                <p><strong>7) Technical and operational data (third-party service providers, third-party service providers, third-party service providers)</strong></p>
                <p>
                  To operate and secure PenguinLearn, our infrastructure providers (including <strong>third-party service providers</strong>)
                  may process technical data such as IP address, device and browser information, request logs, timestamps, error reports, and security signals.
                  We use this information to keep the Services running, improve performance, prevent abuse, and protect user accounts.
                </p>

                <p><strong>What we do not do</strong></p>
                <ul>
                  <li>We do <strong>not</strong> buy or lease personal data from third parties.</li>
                  <li>We do <strong>not</strong> purchase mailing lists.</li>
                  <li>We do <strong>not</strong> match email addresses to physical addresses for marketing.</li>
                  <li>We do <strong>not</strong> ask for or collect phone numbers or postal addresses for accounts.</li>
                </ul>
              </div>

              <button className="collapsible" id="sharing-your-data-button">Sharing Your Data</button>
              <div className="content-collapsible" id="sharing-your-data">
                <p>We may share personal information in the following limited situations:</p>

                <p><strong>1) Teachers (to provide classes)</strong></p>
                <p>
                  When a Parent/Guardian enrolls a Learner in a class, Teachers may receive the limited information necessary to run the class
                  (for example, participant names, age group context, and class-related communications).
                </p>

                <p><strong>2) Other class participants</strong></p>
                <p>
                  In live classes, participants may be able to see and hear one another depending on class settings and how the class is conducted
                  (for example, video/audio, display names, and chat).
                </p>

                <p><strong>3) Service providers</strong></p>
                <p>
                  We use service providers to help operate PenguinLearn, including:
                </p>
                <ul>
                  <li><strong>Zoom</strong> (live classes and recordings)</li>
                  <li><strong>Stripe</strong> (payment processing)</li>
                  <li><strong>Third-party service providers</strong> (database and storage infrastructure)</li>
                  <li><strong>Third-party service providers</strong> (hosting and application infrastructure)</li>
                  <li><strong>Third-party service providers</strong> (security and performance services)</li>
                  <li><strong>Third-party service providers</strong> (email communications)</li>
                </ul>
                <p>
                  These providers process personal information only as needed to provide services to us, and we expect them to protect the information they handle.
                </p>

                <p><strong>4) Background check providers (Teachers)</strong></p>
                <p>
                  If a Teacher completes a background check, personal information may be shared with the screening provider to complete the check.
                </p>

                <p><strong>5) Legal, safety, and compliance</strong></p>
                <p>
                  We may disclose personal information if required by law or legal process, or if necessary to protect the safety and security of our users,
                  prevent fraud, enforce our policies, or protect our rights.
                </p>

                <p><strong>6) Business transfers</strong></p>
                <p>
                  If PenguinLearn is involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, information may be transferred
                  as part of that transaction. We will take steps to require appropriate protections in connection with such a transfer.
                </p>
              </div>

              <button className="collapsible" id="where-is-your-data-button">Where In The World Is Your Data?</button>
              <div className="content-collapsible" id="where-is-your-data">
                <p>
                  PenguinLearn is based in the United States. Our service providers may process data in the United States and other countries.
                  If you use PenguinLearn from outside the United States, your information may be transferred to and processed in the United States
                  and other jurisdictions where our service providers operate.
                </p>
              </div>

              <button className="collapsible" id="your-rights-button">Your Rights and How to Manage or Delete Your Personal Information</button>
              <div className="content-collapsible" id="your-rights">
                <p>
                  Depending on where you live, you may have rights to access, correct, or delete your personal information.
                </p>
                <p>
                  To request access, correction, or deletion (including requests relating to a Learner), contact us at{" "}
                  <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
                  We may need to verify your identity before completing a request.
                </p>
                <p>
                  Deleting an account may remove access to classes, class history, and related records, subject to legal and operational retention needs
                  (for example, fraud prevention, dispute resolution, and safety).
                </p>
              </div>

              <button className="collapsible" id="childrens-privacy-button">Children&apos;s Privacy</button>
              <div className="content-collapsible" id="childrens-privacy">
                <p>
                  PenguinLearn is designed for children with Parent/Guardian involvement. Parents/Guardians control account creation and enrollment.
                </p>
                <p>
                  If you believe a child has provided personal information without appropriate Parent/Guardian involvement, please contact us at{" "}
                  <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a> and we will work to address the issue.
                </p>
                <p>
                  Please note that classes are recorded. Parents/Guardians should review class details and platform notices before enrolling.
                </p>
              </div>

              <button className="collapsible" id="contact-button">Contact Us</button>
              <div className="content-collapsible">
                <p>
                  If you have questions about this Policy or how we handle personal information, contact us at{" "}
                  <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
                </p>
              </div>

              <button className="collapsible" id="secure-data-button">Keeping and Securing Your Data</button>
              <div className="content-collapsible">
                <p>
                  We take reasonable measures to help protect personal information from loss, misuse, and unauthorized access.
                  However, no system can be guaranteed to be 100% secure. You are responsible for keeping your login credentials confidential.
                </p>
                <p>
                  We retain personal information for as long as needed to provide the Services, comply with legal obligations, resolve disputes,
                  enforce our agreements, and maintain platform safety. You may request deletion as described above.
                </p>
              </div>

              <button className="collapsible" id="supplemental-notices-button">Supplemental Notices</button>
              <div className="content-collapsible">
                <p>
                  Additional notices may apply depending on your location. For example:
                </p>
                <ul>
                  <li><a href="../footer/ca-privacy">For California</a></li>
                </ul>
                <p>
                  If you would like more information about location-specific privacy rights, contact{" "}
                  <a href="mailto:support@penguinlearn.com">support@penguinlearn.com</a>.
                </p>
              </div>
            </div>
          </main>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;